<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="form.id">{{ $t('common.change')}} {{tenant.name}}</h1>
      <h1 v-else>{{ $t('add_tenant.new_tenant')}}</h1>
    </div>
    <div class="details-component__content">
      <form class="form">
        <fieldset class="form__fieldset">

          <div class="form__field" :class="{'form__field_error' : errors.has('name')}">
            <label for="name" class="form__label">{{ $t('common.tenant')}}</label>
            <input type="text" class="form__input" v-model.trim="form.name" id="name"
            v-validate="'required|min:3|max:255'" data-vv-as="Rentniku nimi" data-vv-validate-on="none" name="name"  />
            <tooltip v-show="errors.has('name:required')" class="form__validation">{{ $t('errors.name_required') }}</tooltip>
            <tooltip v-show="errors.has('name:min')" class="form__validation">{{ $t('errors.name_min') }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('contactPerson')}">
            <label for="contactPerson" class="form__label">{{ $t('add_tenant.contact_person')}}</label>
            <input type="text" class="form__input" v-model.trim="form.contactPerson" id="contactPerson" v-validate="'min:3'" data-vv-as="Kontaktisik" name="contactPerson"  />
            <tooltip v-show="errors.has('contactPerson:min')" class="form__validation">{{ $t('errors.name_min') }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('address')}">
            <label for="address" class="form__label">{{ $t('common.address')}}</label>
            <input type="text" class="form__input" v-model.trim="form.address" id="address" v-validate data-vv-rules="min:3|max:255" data-vv-as="Aadress" name="address"  />
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('regCode')}">
            <label for="reg-code" class="form__label">{{ $t('tenant_details.reg_code')}}</label>
            <input type="text" class="form__input" v-model.trim="form.regCode" id="reg-code" v-validate data-vv-rules="max:255" data-vv-as="RegCode" name="regCode"  />
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('legalAddress')}">
            <label for="legal-address" class="form__label">{{ $t('tenant_details.legal_address')}}</label>
            <input type="text" class="form__input" v-model.trim="form.legalAddress" id="legal-address" v-validate data-vv-rules="min:3|max:255" data-vv-as="LegalAadress" name="legalAddress"  />
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('vatNumber')}">
            <label for="vat-number" class="form__label">{{ $t('tenant_details.vat_number')}}</label>
            <input type="text" class="form__input" v-model.trim="form.vatNumber" id="vat-number" v-validate data-vv-rules="max:255" data-vv-as="VatNumber" name="vatNumber"  />
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('phone')}">
            <label for="phone" class="form__label">{{ $t('common.phone')}}</label>
            <input type="text" class="form__input" v-model.trim="form.phoneNumber" id="phone" v-validate="'validPhoneNr'" data-vv-validate-on="blur|focus" data-vv-as="Telefon" name="phone"  />
            <tooltip v-show="errors.has('phone')" class="form__validation">{{ $t('errors.phone_field') }}</tooltip>
          </div>

          <div class="form__field" :class="{'form__field_error' : errors.has('email')}">
            <label for="email" class="form__label">{{ $t('common.email')}}</label>
            <input type="email" class="form__input" v-model.trim="form.email" id="email" v-validate="'required|email|min:3|max:255'" data-vv-as="Email" name="email" data-vv-validate-on="blur"  />
            <tooltip v-show="errors.has('email')" class="form__validation">{{ $t('errors.email_field') }}</tooltip>
          </div>
        </fieldset>

        <div v-if="tenantError" class="orange">{{ $t('add_tenant.failed_to_save_tenant') }}</div>

        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'tenants' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'tenant', params: { id: $route.params.id} }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="onSubmit()">
            {{ $t('common.save') }}
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { parse, format, isValidNumber } from 'libphonenumber-js';
import { mapGetters } from 'vuex';
import 'styles/blocks/details.scss';
import * as mTypes from 'store/mutation-types';
import Autocomplete from 'common/Autocomplete';
import Tooltip from 'common/Tooltip';

export default {
  components: {
    Autocomplete,
    Tooltip,
  },
  watch: {
    $route: 'fetchAllData',
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    ...mapGetters({
      tenant: 'tenants/tenant/tenant',
      tenantError: 'tenants/tenant/error',
      user: 'user/user/user',
    }),
  },
  data() {
    return {
      form: {},
    };
  },
  mounted() {
    this.fetchAllData();
  },
  created() {
    this.$store.commit(`tenants/tenant/${mTypes.CLEAR_STATE}`);
    this.$validator.remove('validPhoneNr');
    this.$validator.extend('validPhoneNr', {
      getMessage: () => 'Please insert valid phone number. Mobile number must include country code.',
      validate: (value) => {
        if (value.trim() === '') {
          return true;
        }
        const parsed = parse(value.trim(), { country: { default: 'EE' } });
        return isValidNumber(parsed);
      },
    });
  },
  methods: {
    fetchAllData() {
      if (this.$route.params.id) {
        this.fetchTenant();
      } else {
        this.$store.commit(`tenants/tenant/${mTypes.CLEAR_STATE}`);
        this.form = this.tenant;
      }
    },
    fetchTenant() {
      this.$store.dispatch('tenants/tenant/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
        this.form = this.tenant;
        this.formatMobileNrForForm();
      });
    },
    onSubmit() {
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.form.partner_id = this.user.data.selectedRole.id;
          this.parsePhoneBeforeSave();
          this.$store.dispatch('tenants/tenant/saveTenant', { tenant: this.form, user: this.user }).then(() => {
            if (!this.tenantError) {
              this.form = {};
              this.$store.commit(`landlord/tenants/${mTypes.EDIT_DATA}`, this.tenant);
              this.$router.push({ name: 'tenant', params: { id: this.tenant.id } });
            }
          }).catch(() => {
            this.$store.commit(`tenants/tenant/${mTypes.DATA_ERROR}`, true);
          });
          this.$validator.clean();
          this.$store.commit(`tenants/tenant/${mTypes.DATA_ERROR}`, null);
        }).catch(() => {
        });
    },
    parsePhoneBeforeSave() {
      const parsedPhone = parse(this.form.phoneNumber, { country: { default: 'EE' } });
      this.form.phoneCountry = parsedPhone.country;
      this.form.phone = parsedPhone.phone;
    },
    formatMobileNrForForm() {
      if (this.form.phone) {
        this.form.phoneNumber = format({ country: this.form.phoneCountry, phone: this.form.phone.toString() }, 'International_plaintext');
      } else {
        this.form.phoneNumber = '';
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/tenants/AddTenantView.vue